// Buscador global: mascotas, clientes, teléfonos y conversaciones de las clínicas que estás viendo.
// Se abre con el botón "Buscar" o con Ctrl/⌘ + K. Solo carga datos cuando se abre.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, limit } from './fs';
import { db } from './firebase';
import { nombreClinica } from './clinicas';
import Icono from './Iconos.jsx';

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const digitos = (s) => String(s || '').replace(/\D/g, '');

async function cargar(col, clinicas, filtros = [], n = 300) {
  try {
    const q = query(collection(db, col), where('clinica', 'in', clinicas), ...filtros.map(([a, b, c]) => where(a, b, c)), limit(n));
    return (await getDocs(q)).docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch { return []; }
}

// Qué se busca en cada sitio y adónde lleva
const FUENTES = [
  { col: 'hospitalizados', filtros: [['activo', '==', true]], tipo: 'Hospitalizado', icono: 'hospital',
    campos: (x) => [x.mascota, x.cliente, x.telefono], titulo: (x) => x.mascota, sub: (x) => [x.cliente, x.jaula, x.motivo].filter(Boolean).join(' · '), ir: (x) => `/hospitalizacion?h=${x.id}` },
  { col: 'pendientes', tipo: 'Prueba / encargo', icono: 'resultados', filtros: [['abierto', '==', true]],
    campos: (x) => [x.mascota, x.cliente, x.telefono, x.que], titulo: (x) => x.mascota || x.cliente, sub: (x) => [x.que, x.cliente, x.estado].filter(Boolean).join(' · '), ir: () => '/pendientes' },
  { col: 'conversaciones', tipo: 'WhatsApp', icono: 'whatsapp',
    campos: (x) => [x.alias, x.nombre, x.telefono, ...(x.mascotas || [])], titulo: (x) => x.alias || x.nombre || `+${x.telefono}`, sub: (x) => x.resumen || x.ultimoMensaje || '', ir: (x) => `/whatsapp?c=${x.id}` },
  { col: 'igualas', tipo: 'Iguala', icono: 'iguala',
    campos: (x) => [x.mascota, x.cliente, x.telefono], titulo: (x) => x.mascota || x.cliente, sub: (x) => [x.cliente, x.plan].filter(Boolean).join(' · '), ir: () => '/igualas' },
  { col: 'llamadas', filtros: [['hecha', '==', false]], tipo: 'Llamada pendiente', icono: 'dia',
    campos: (x) => [x.quien, x.telefono, x.motivo], titulo: (x) => x.quien || x.telefono, sub: (x) => [x.motivo, x.para && `para ${x.para}`].filter(Boolean).join(' · '), ir: () => '/dia' },
];

export default function Buscador({ abierto, onCerrar, clinicas, modulos }) {
  const [t, setT] = useState('');
  const [datos, setDatos] = useState(null);
  const [i, setI] = useState(0);
  const ir = useNavigate();
  const input = useRef(null);

  useEffect(() => {
    if (!abierto) return;
    setT(''); setI(0); setTimeout(() => input.current?.focus(), 20);
    let vivo = true;
    // Solo se busca en lo que la persona puede abrir (el equipo no tiene igualas: llevan importes)
    const fuentes = FUENTES.map((f) => (f.col === 'igualas' && !modulos.some((m) => m.path === '/igualas') ? null : f));
    Promise.all(fuentes.map((f) => (f ? cargar(f.col, clinicas, f.filtros) : Promise.resolve([])))).then((r) => vivo && setDatos(r));
    return () => { vivo = false; };
  }, [abierto, clinicas.join(',')]);

  const res = useMemo(() => {
    const q = norm(t.trim()); const qd = digitos(t);
    const mods = modulos.filter((m) => !q || norm(m.label).includes(q)).map((m) => ({ k: `m${m.path}`, tipo: 'Ir a', icono: m.icono, titulo: m.label, ir: m.path }));
    if (q.length < 2) return mods.slice(0, 8);
    const out = [];
    FUENTES.forEach((f, n) => {
      for (const x of datos?.[n] || []) {
        const ok = f.campos(x).some((c) => norm(c).includes(q) || (qd.length >= 4 && digitos(c).includes(qd)));
        if (ok) out.push({ k: `${f.col}${x.id}`, tipo: f.tipo, icono: f.icono, titulo: f.titulo(x), sub: f.sub(x), clinica: x.clinica, ir: f.ir(x) });
        if (out.length > 40) break;
      }
    });
    return [...mods, ...out].slice(0, 30);
  }, [t, datos, modulos]);

  useEffect(() => { setI(0); }, [t]);
  if (!abierto) return null;
  const elegir = (r) => { if (!r) return; onCerrar(); ir(r.ir); };

  return (
    <div className="bus-fondo" onMouseDown={onCerrar}>
      <div className="bus" role="dialog" aria-label="Buscar" onMouseDown={(e) => e.stopPropagation()}>
        <div className="bus-campo">
          <Icono n="buscar" />
          <input ref={input} value={t} onChange={(e) => setT(e.target.value)} placeholder="Mascota, cliente, teléfono o pantalla…"
            onKeyDown={(e) => {
              if (e.key === 'Escape') onCerrar();
              if (e.key === 'ArrowDown') { e.preventDefault(); setI((x) => Math.min(x + 1, res.length - 1)); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setI((x) => Math.max(x - 1, 0)); }
              if (e.key === 'Enter') elegir(res[i]);
            }} />
          <button className="bus-x" onClick={onCerrar} aria-label="Cerrar">Esc</button>
        </div>
        <ul className="bus-lista">
          {t.trim().length >= 2 && datos === null && <li className="bus-vacio">Buscando…</li>}
          {t.trim().length >= 2 && datos && res.length === 0 && <li className="bus-vacio">Nada con «{t}».</li>}
          {res.map((r, n) => (
            <li key={r.k}>
              <button className={n === i ? 'on' : ''} onMouseEnter={() => setI(n)} onClick={() => elegir(r)}>
                <Icono n={r.icono} />
                <span className="bus-txt"><b>{r.titulo}</b>{r.sub && <small>{r.sub}</small>}</span>
                <span className="bus-tipo">{r.tipo}{r.clinica && clinicas.length > 1 ? ` · ${nombreClinica(r.clinica)}` : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
